/**
 * DECISIONS 401 (the owner, after 394's trial: "Motion first, then decide"): the clip layer — the CC0 gait clips
 * (personClipsData.ts, baked by scripts/bakePersonClips.ts) drive today's soldier's hips, torso and legs while it walks or
 * runs; the head's look, the arms on the gun's IK and everything else stay today's. No skeleton a figure: one table a clip,
 * shared by every figure, sampled at the stride's phase (personModel's gaitPhase — the left foot's strike at 0, so the
 * footsteps keep their beat) and blended by the gait's speed between the clips either side of it (standing: the walk, the
 * jog, the sprint; crouched: the crouch walk). A presentation flag, off by default: the Lab's row (`personClips`) or
 * `?clips=1`.
 */
import * as THREE from 'three';
import { ACTION_WALK_SPEED, GAIT as PLAY_GAIT, STANCE_SPEED } from '../constants/action';
import { PERSON_BODY_HEIGHT } from '../constants/physics';
import { PERSON_CLIPS, PERSON_CLIP_JOINTS, type PersonClipData } from './personClipsData';

let override: boolean | null = null;
/** The Lab's launch: `null` goes back to the URL / the default (off). */
export function setPersonClips(on: boolean | null): void { override = on; }
/** Whether the clip layer drives the gait: the Lab's override, else the page's `?clips=1`. */
export function personClipsOn(): boolean {
  if (override !== null) return override;
  if (fromUrl === null) try { fromUrl = typeof location !== 'undefined' && new URLSearchParams(location.search).get('clips') === '1'; } catch { fromUrl = false; }
  return fromUrl;
}
/** The page's `?clips=1`, read once (every figure asks every frame). */
let fromUrl: boolean | null = null;

export type PersonClipJoint = typeof PERSON_CLIP_JOINTS[number];
/**
 * A sampled pose: each joint's turn from its parent's (PERSON_CLIP_JOINTS' order) and the hips' x, height, z (person units);
 * 410: the clips it was blended from (`b` at share `k`; none: `a` alone) and the thighs' `widen` still to apply (widenStride).
 */
export interface PersonClipPose { q: THREE.Quaternion[]; hip: THREE.Vector3; a: PersonClipData; b: PersonClipData | null; k: number; widen: number }
export function createClipPose(): PersonClipPose { return { q: PERSON_CLIP_JOINTS.map(() => new THREE.Quaternion()), hip: new THREE.Vector3(), a: PERSON_CLIPS.walk, b: null, k: 0, widen: 1 }; }

/**
 * DECISIONS 410 (1), the walk at play's pace: play's paces are faster than the clips' — the aimed walk (5 u/s at a person
 * 1.1 tall: 8.2 person units a second) is past the clips' jog (5.9) — so blended by speed every standing figure showed the
 * sprint's stride. Each clip is drawn at a pace of play's instead (person units a second): the walk at the aimed walk, the
 * jog at the move (GAIT.move, the gun low), the sprint at the sprint, the crouch walk at the crouched walk; between two, a
 * blend by the speed's place between their paces. `widen`: the thighs' swing at that pace over the clip's (a longer stride,
 * the cadence still the footsteps' beat), from 1 at the clip's own speed; the rest of the gap slides, as today's gait's feet
 * do past its cap. Starting dials, the owner's to tune.
 */
const WALK_PACE = (ACTION_WALK_SPEED / PERSON_BODY_HEIGHT) * 1.8;
export const CLIP_PACE: Record<'walk' | 'jog' | 'sprint' | 'crouch', { pace: number; widen: number }> = {
  walk: { pace: WALK_PACE, widen: 1.45 },
  jog: { pace: WALK_PACE * PLAY_GAIT.move.speed, widen: 1.1 },
  sprint: { pace: WALK_PACE * PLAY_GAIT.sprint.speed, widen: 1.15 },
  crouch: { pace: WALK_PACE * STANCE_SPEED.crouch, widen: 1.3 },
};

/** The clips a stance blends between, slowest first, each with its pace. */
const BY_STANCE: Record<'stand' | 'crouch', ReadonlyArray<{ clip: PersonClipData; pace: number; widen: number }>> = {
  stand: [{ clip: PERSON_CLIPS.walk, ...CLIP_PACE.walk }, { clip: PERSON_CLIPS.jog, ...CLIP_PACE.jog }, { clip: PERSON_CLIPS.sprint, ...CLIP_PACE.sprint }],
  crouch: [{ clip: PERSON_CLIPS.crouch, ...CLIP_PACE.crouch }],
};

const _a = new THREE.Quaternion(), _b = new THREE.Quaternion(), _v = new THREE.Vector3(), _other = createClipPose();

/** One clip at the stride's `phase` (0 … 1: the left foot's strike at 0), between its two nearest samples. */
function sampleOne(c: PersonClipData, phase: number, out: PersonClipPose): void {
  const at = ((((c.strike + phase / c.strides) % 1) + 1) % 1) * c.frames;
  const i0 = Math.floor(at) % c.frames, i1 = (i0 + 1) % c.frames, k = at - Math.floor(at), n = PERSON_CLIP_JOINTS.length;
  for (let j = 0; j < n; j++) {
    const o0 = (i0 * n + j) * 4, o1 = (i1 * n + j) * 4;
    _a.set(c.q[o0], c.q[o0 + 1], c.q[o0 + 2], c.q[o0 + 3]);
    _b.set(c.q[o1], c.q[o1 + 1], c.q[o1 + 2], c.q[o1 + 3]);
    out.q[j].slerpQuaternions(_a, _b, k).normalize();   // (the table is rounded to 4 places)
  }
  out.hip.set(c.hip[i0 * 3], c.hip[i0 * 3 + 1], c.hip[i0 * 3 + 2]).lerp(_v.set(c.hip[i1 * 3], c.hip[i1 * 3 + 1], c.hip[i1 * 3 + 2]), k);
}

/**
 * The gait's pose from the clips at `speed` (person units a second) and the stride's `phase`: the two clips whose paces
 * (CLIP_PACE, 410) stand either side of it, blended by where it falls between them (below the slowest, the slowest, its stride
 * shortened toward the clip's own at its own speed; above the fastest, the fastest). The thighs' widening is left in
 * `out.widen` (the clip as baked first: the feet's fit reads its heights from it).
 */
export function sampleGaitClips(stance: 'stand' | 'crouch', speed: number, phase: number, out: PersonClipPose): void {
  const list = BY_STANCE[stance];
  let i = 0;
  while (i < list.length - 1 && speed >= list[i + 1].pace) i++;
  const lo = list[i];
  sampleOne(lo.clip, phase, out);
  out.a = lo.clip; out.b = null; out.k = 0;
  out.widen = 1 + (lo.widen - 1) * Math.max(0, Math.min(1, (speed - lo.clip.speed) / (lo.pace - lo.clip.speed)));
  if (i === list.length - 1 || speed <= lo.pace) return;
  const hi = list[i + 1], k = (speed - lo.pace) / (hi.pace - lo.pace);
  sampleOne(hi.clip, phase, _other);
  for (let j = 0; j < out.q.length; j++) out.q[j].slerp(_other.q[j], k);
  out.hip.lerp(_other.hip, k);
  out.b = hi.clip; out.k = k; out.widen = lo.widen + (hi.widen - lo.widen) * k;
}

/**
 * 410 (1): the thighs' swing scaled by the pose's `widen` — each thigh's turn from its mean over the loop (the blend's), its
 * angle × widen: a crouch's thighs, near level on average, swing wider about that, not further up.
 */
export function widenStride(pose: PersonClipPose): void {
  if (pose.widen === 1) return;
  for (const j of THIGHS) {
    const mean = _m.copy(clipMean(pose.a, j));
    if (pose.b) mean.slerp(clipMean(pose.b, j), pose.k);
    const d = _d.copy(mean).invert().multiply(pose.q[j]);
    if (d.w < 0) d.set(-d.x, -d.y, -d.z, -d.w);   // (the short way round)
    const half = Math.acos(Math.max(-1, Math.min(1, d.w))), s = Math.sin(half);
    if (s < 1e-6) continue;
    const to = half * pose.widen, k = Math.sin(to) / s;
    pose.q[j].copy(mean.multiply(d.set(d.x * k, d.y * k, d.z * k, Math.cos(to))));
  }
}
const _m = new THREE.Quaternion(), _d = new THREE.Quaternion();
const THIGHS = [PERSON_CLIP_JOINTS.indexOf('thighL'), PERSON_CLIP_JOINTS.indexOf('thighR')];

/**
 * DECISIONS 410 (3): standing still or crouched, the library's idle at `seconds` into its loop (the figure's own clock): its
 * breathing and its weight shifting from foot to foot.
 */
export function sampleIdleClip(stance: 'stand' | 'crouch', seconds: number, out: PersonClipPose): void {
  const c = stance === 'crouch' ? PERSON_CLIPS.crouchIdle : PERSON_CLIPS.idle;
  sampleOne(c, seconds / c.seconds, out);
  out.a = c; out.b = null; out.k = 0; out.widen = 1;
}

const means = new Map<PersonClipData, THREE.Quaternion[]>();
/** A clip's mean turn of each joint over its loop (normalised sum, the short way round): what a layer adds is the clip less it. */
export function clipMean(c: PersonClipData, joint: number): THREE.Quaternion {
  let list = means.get(c);
  if (!list) {
    list = PERSON_CLIP_JOINTS.map((_, j) => {
      const sum = new THREE.Quaternion(0, 0, 0, 0), q = new THREE.Quaternion();
      for (let f = 0; f < c.frames; f++) {
        const o = (f * PERSON_CLIP_JOINTS.length + j) * 4;
        q.set(c.q[o], c.q[o + 1], c.q[o + 2], c.q[o + 3]);
        const sign = sum.x * q.x + sum.y * q.y + sum.z * q.z + sum.w * q.w < 0 ? -1 : 1;
        sum.set(sum.x + sign * q.x, sum.y + sign * q.y, sum.z + sign * q.z, sum.w + sign * q.w);
      }
      return sum.normalize();
    });
    means.set(c, list);
  }
  return list[joint];
}
